// What to do with a running session when the auth key-exchange fails (ENG-8260).
//
// Extracted as a pure function so the decision table is unit-testable without
// tmux, a manager tick, or the 15k-line `manager-worker` module. The call site
// (`ensurePersistentSession`) supplies the two observations and executes the
// returned action.
//
// THE BUG THIS REPLACES
// --------------------
// The pre-ENG-8260 catch block did exactly one thing on any `/host/exchange`
// failure:
//
//     if (isSessionHealthy(codeName)) { stopPersistentSessionAndForgetMcpBaseline(codeName); ... }
//     return { decision: 'skipped-auth-resolve-failed', ... }
//
// `skipped-auth-resolve-failed` is the correct decision for a SPAWN — without
// resolved auth there is nothing to spawn with. But the teardown was
// unconditional on the failure's cause, and the two cases are not the same:
//
//   * No session running       → can't spawn. Log and skip. Always correct.
//   * Session running, healthy  → the session already holds its credentials in
//     its own process env (`persistent-session.ts` passes `-e ANTHROPIC_API_KEY`
//     into the tmux env at spawn time, and subscription mode reads the on-disk
//     OAuth creds). A failed exchange for a FUTURE spawn tells us nothing about
//     it. Stopping it destroys in-flight work and buys nothing.
//
// On 2026-07-29 a ~2s HTTP 500 blip on `alyve-host-1` therefore SIGINT'd
// `dwight` mid-tool-call, cancelled two runs, killed its stdio MCP channel
// servers, and left the agent re-deriving the same in_progress card from
// scratch 29s later. It had reported `decision=healthy` on every poll for the
// preceding 40+ minutes.
//
// WHAT IS DELIBERATELY UNCHANGED
// ------------------------------
// A genuine verdict (401/403, revoked key) with a healthy session STILL tears
// the session down. That is the ENG-4417 fail-closed posture — a session
// running under credentials the control plane has rejected must not keep
// running — and this change does not weaken it. Only the transient branch is
// new, and it only fires on positive evidence of a platform fault
// (`isTransientAuthFailure`, which returns false for any error shape it cannot
// classify).

import { isTransientAuthFailure } from '../auth-exchange-error.js';

/**
 * The action `ensurePersistentSession` should take after an auth-resolve
 * failure. Exhaustive — every combination of (transient?, healthy?) maps to
 * exactly one of these.
 */
export type AuthResolveFailureAction =
  /**
   * Transient platform fault + a healthy session exists. Leave the session
   * completely alone: no stop, no spawn, no breaker record. The next poll
   * (~30s) retries the exchange. THE ENG-8260 FIX.
   */
  | 'preserve-session'
  /**
   * A verdict on the credential + a healthy session exists. Stop it, as the
   * pre-ENG-8260 code did for every failure. Next successful poll respawns.
   */
  | 'stop-session'
  /**
   * No healthy session to preserve or stop. Skip the spawn. Identical to
   * today's behaviour for this case (the old code's `isSessionHealthy` guard
   * already made the stop a no-op here).
   */
  | 'skip-spawn';

export interface AuthResolveFailureDecision {
  action: AuthResolveFailureAction;
  /**
   * True when the failure carried positive evidence of a transient platform
   * fault. Surfaced so the caller can log it without re-classifying.
   */
  transient: boolean;
  /** Short non-secret tag for the `[persistent-session-decision]` log line. */
  reason:
    | 'transient-fault-session-preserved'
    | 'transient-fault-no-session'
    | 'auth-verdict-session-stopped'
    | 'auth-verdict-no-session';
}

/**
 * Decide what an auth-resolve failure means for the session that is (or is not)
 * currently running.
 *
 * @param error         The error thrown out of the exchange path. Classified via
 *                      `isTransientAuthFailure`, which is conservative: an
 *                      unrecognised shape counts as a verdict, so the
 *                      session-preserving branch never fires on the mere
 *                      absence of evidence.
 * @param sessionHealthy Whether a healthy persistent session exists right now,
 *                      as observed by the caller immediately before this call.
 */
export function decideAuthResolveFailureAction(args: {
  error: unknown;
  sessionHealthy: boolean;
}): AuthResolveFailureDecision {
  const transient = isTransientAuthFailure(args.error);

  if (!args.sessionHealthy) {
    return {
      action: 'skip-spawn',
      transient,
      reason: transient ? 'transient-fault-no-session' : 'auth-verdict-no-session',
    };
  }

  if (transient) {
    return {
      action: 'preserve-session',
      transient,
      reason: 'transient-fault-session-preserved',
    };
  }

  return {
    action: 'stop-session',
    transient,
    reason: 'auth-verdict-session-stopped',
  };
}
